import { Dispatch, SetStateAction, useCallback } from 'react';
import { useDrop } from 'react-dnd';
import cn from 'classnames';
import { MetricsTablePreset, SortInfo } from '@blisspointmedia/bpm-types/dist/MetricsTable';
import { useSortAdder } from '@Components/MetricsTable/metrics-table-utils';
import {
  COLUMN_DND_TYPE,
  DIMENSION_COLUMN_DND_TYPE
} from '@src/utils/columnMetricSystem/configUtils';
import { SortItem } from './SortItem';

interface SortingBoxProps {
  colorMap: Record<string, string | undefined>;
  presetChanges: MetricsTablePreset;
  setHoverItem: Dispatch<SetStateAction<string | undefined>>;
  setPresetChanges: Dispatch<SetStateAction<MetricsTablePreset>>;
}

export const SortingBox = ({
  presetChanges,
  setPresetChanges,
  setHoverItem,
  colorMap
}: SortingBoxProps) => {
  const newSortInfo = presetChanges.defaultSorting ?? [];
  const setNewSortInfo = useCallback(
    (func: SortInfo[] | ((arr: SortInfo[]) => SortInfo[])) => {
      setPresetChanges(changes => ({
        ...changes,
        defaultSorting: typeof func === 'function' ? func(changes.defaultSorting ?? []) : func
      }));
    },
    [setPresetChanges]
  );
  const addSorting = useSortAdder(setNewSortInfo);
  const [{ isOver }, drop] = useDrop<
    { type: typeof COLUMN_DND_TYPE | typeof DIMENSION_COLUMN_DND_TYPE; id: string },
    void,
    { isOver: boolean }
  >({
    accept: [COLUMN_DND_TYPE, DIMENSION_COLUMN_DND_TYPE],
    canDrop: item => !newSortInfo.some(existing => existing.id === item.id),
    collect: monitor => ({
      isOver: monitor.isOver() && monitor.canDrop()
    }),
    drop: item => addSorting(item.id)
  });
  const dropRef = useCallback(
    (node: HTMLDivElement | null) => {
      drop(node);
    },
    [drop]
  );
  return (
    <div className={cn('sorting-box', { 'is-over': isOver })} ref={dropRef}>
      <div>Sort Order</div>
      {newSortInfo.length === 0 && <div className='drag-here'>Drag and drop here</div>}
      <div className='sort-item-box'>
        {newSortInfo.map(item => (
          <SortItem
            colorMap={colorMap}
            item={item}
            key={item.id}
            onClick={() => addSorting(item.id)}
            setHoverItem={setHoverItem}
            setNewSortInfo={setNewSortInfo}
          />
        ))}
      </div>
    </div>
  );
};
